import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  NgModule,
} from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import {
  DataObjectTypeName,
  DataObjectTypes,
} from 'src/app/models/dataObject.model';
import { ModelResultDataObject } from 'src/app/models/modelResultDataObject.model';
import { TimeSeriesRevision } from 'src/app/models/timeSeriesRevision.model';
import {
  timeSeriesAnalyticsSandboxFailed,
  timeSeriesAnalyticsSandboxIsProcessing,
  timeSeriesAnalyticsSandboxSucceeded,
  isTimeSeriesInAnalyticsSandbox,
  isInAnalyticsSandbox,
  analyticsSandboxIsProcessing,
  analyticsSandboxSucceeded,
  analyticsSandboxFailed,
  isEmptyNullOrUndefined,
} from 'src/app/shared/utils/utils';
import { ValuationRunStatusValues } from 'src/app/models/valuationRun.model';
import { IconsModule } from '../icons/icons.component.module';
import { ScenarioResultSetDataObject } from 'src/app/models/scenarioResultDataObject.model';
import { CalculatedTimeseriesDataObject } from 'src/app/models/calculatedTimeseriesDataObject.model';
import {
  isTimeSeriesObjectType,
  TIME_SERIES_DATA_OBJECTS,
} from 'src/app/models/timeSeriesDataObject.model';
import { EscalatedForwardCurveAsOfDate } from 'src/app/models/escalatedForwardCurveReference.model';
import { HistoricDatasetGenerationProfile } from 'src/app/models/historicDataset.model';

@Component({
  selector: 'app-dataobject-icon',
  template: `
    <div class="flex">
      <ng-container [ngSwitch]="objectType">
        <ng-container *ngSwitchCase="dataObjectTypeName.ForwardCurveReference">
          <app-forwardcurvereference-icon></app-forwardcurvereference-icon>
        </ng-container>
        <ng-container *ngSwitchCase="dataObjectTypeName.VolatilityReference">
          <app-volatilityreference-icon></app-volatilityreference-icon>
        </ng-container>
        <ng-container *ngSwitchCase="dataObjectTypeName.HistoricDataset">
          <app-historicdataset-icon
            [class.text-red-400]="
              dataObject.objectBody.isHistoricDatasetValid === false
            "
            [title]="
              dataObject.objectBody.isHistoricDatasetValid === false
                ? 'This Historic Dataset is invalid.'
                : ''
            "
          ></app-historicdataset-icon>
        </ng-container>
        <ng-container *ngSwitchCase="timeSeriesDataObjectTypeNamesCombined">
          <app-timeseries-icon></app-timeseries-icon>
        </ng-container>
        <ng-container
          *ngSwitchCase="dataObjectTypeName.HistoricDatasetGenerationProfile"
        >
          <app-timeseries-icon></app-timeseries-icon>
        </ng-container>
        <ng-container *ngSwitchCase="dataObjectTypeName.DealTerm">
          <app-dealterm-icon></app-dealterm-icon>
        </ng-container>
        <ng-container *ngSwitchCase="dataObjectTypeName.ModelCalibration">
          <app-modelcalibration-icon
            [class.text-red-400]="
              dataObject.objectBody.isModelCalibrationValid === false
            "
            [title]="
              dataObject.objectBody.isModelCalibrationValid === false
                ? 'This Model Calibration is invalid.'
                : ''
            "
          ></app-modelcalibration-icon>
        </ng-container>
        <ng-container *ngSwitchCase="dataObjectTypeName.ValuationTask">
          <app-valuationtask-icon></app-valuationtask-icon>
        </ng-container>
        <ng-container *ngSwitchCase="dataObjectTypeName.ValuationRun">
          <app-execute-valuationtask-icon></app-execute-valuationtask-icon>
        </ng-container>
        <ng-container *ngSwitchCase="dataObjectTypeName.ModelResult">
          <app-model-result-icon></app-model-result-icon>
        </ng-container>
        <ng-container *ngSwitchCase="dataObjectTypeName.CalculatedCurve">
          <app-calculator-icon></app-calculator-icon>
        </ng-container>
        <ng-container
          *ngSwitchCase="dataObjectTypeName.AggregatedScenarioResult"
        >
          <app-aggregated-scenario-result-icon></app-aggregated-scenario-result-icon>
        </ng-container>
        <ng-container *ngSwitchCase="dataObjectTypeName.ValuationRunResults">
          <app-valuationrun-results-icon></app-valuationrun-results-icon>
        </ng-container>
        <ng-container *ngSwitchCase="dataObjectTypeName.TimeSeriesRevision">
          <app-forwardcurve-revision-icon></app-forwardcurve-revision-icon>
        </ng-container>
        <ng-container
          *ngSwitchCase="dataObjectTypeName.EscalatedForwardCurveReference"
        >
          <app-escalated-forwardcurve-reference-icon></app-escalated-forwardcurve-reference-icon>
        </ng-container>
        <ng-container
          *ngSwitchCase="dataObjectTypeName.EscalatedForwardCurveAsOfDate"
        >
          <app-escalated-forwardcurve-asofdate-icon></app-escalated-forwardcurve-asofdate-icon>
        </ng-container>
      </ng-container>
      <ng-container *ngIf="isInAnalyticsSandbox">
        <span
          class="ml-2"
          [class.text-orange-400]="analyticsSandboxIsProcessing"
        >
          <app-analyticssandbox-icon></app-analyticssandbox-icon>
        </span>
      </ng-container>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataObjectIconComponent {
  @Input() dataObject!: any;
  dataObjectTypeName = DataObjectTypeName;

  constructor() {}

  get isInAnalyticsSandbox() {
    return this.checkDataObjectForAnalyticalSandboxStatus(
      isTimeSeriesInAnalyticsSandbox,
      isInAnalyticsSandbox
    );
  }

  get objectType() {
    if (isTimeSeriesObjectType(this.dataObject)) {
      return this.timeSeriesDataObjectTypeNamesCombined;
    }

    return this.dataObject.objectType;
  }

  get timeSeriesDataObjectTypeNamesCombined() {
    return TIME_SERIES_DATA_OBJECTS.map(([value]) => value).join('|');
  }

  get analyticsSandboxIsProcessing(): boolean {
    return this.checkDataObjectForAnalyticalSandboxStatus(
      timeSeriesAnalyticsSandboxIsProcessing,
      analyticsSandboxIsProcessing
    );
  }

  get isTimeSeriesObjectType(): boolean {
    return isTimeSeriesObjectType(this.dataObject);
  }

  checkDataObjectForAnalyticalSandboxStatus(
    tsRevisionPredicate: any,
    statusPredicate: any
  ): boolean {
    return (
      (this.dataObject.objectType === DataObjectTypeName.ForwardCurve &&
        this.dataObject.timeSeriesMeta?.revisions.filter(tsRevisionPredicate)
          .length > 0) ||
      (this.dataObject.objectType === DataObjectTypeName.ValuationRunResults &&
        this.dataObject.objectBody?.modelResults?.filter(
          (mr: ModelResultDataObject) =>
            mr.timeSeriesMeta.revisions.filter(tsRevisionPredicate).length > 0
        ).length > 0) ||
      (this.dataObject.objectType === DataObjectTypeName.ValuationRunResults &&
        this.dataObject.objectBody?.calculatedtimeseries?.filter(
          (mr: CalculatedTimeseriesDataObject) =>
            mr.timeSeriesMeta.revisions.filter(tsRevisionPredicate).length > 0
        ).length > 0) ||
      (this.dataObject.objectType === DataObjectTypeName.ValuationRunResults &&
        this.dataObject.objectBody?.aggregatedScenarioResult?.aggregatedScenarioResultSet?.filter(
          (mr: ScenarioResultSetDataObject) =>
            statusPredicate(mr.timeSeriesSetMeta.analyticsSandboxStatus)
        ).length > 0)
    );
  }
}

@NgModule({
  declarations: [DataObjectIconComponent],
  exports: [DataObjectIconComponent],
  imports: [CommonModule, IconsModule],
})
export class DataObjectIconComponentModule {}

@Component({
  selector: 'app-aggrid-dataobject-icon',
  template: `<div class="pt-2">
    <app-dataobject-icon [dataObject]="dataObject"></app-dataobject-icon>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridDataObjectIconComponent implements ICellRendererAngularComp {
  dataObject!: DataObjectTypes;

  agInit(params: ICellRendererParams<any, any>): void {
    this.dataObject = params.data as DataObjectTypes;
  }

  refresh(params: ICellRendererParams<any, any>): boolean {
    this.agInit(params);
    return true;
  }
}

@NgModule({
  declarations: [AgGridDataObjectIconComponent],
  exports: [AgGridDataObjectIconComponent],
  imports: [CommonModule, DataObjectIconComponentModule],
})
export class AgGridDataObjectIconComponentModule {}

@Component({
  selector: 'app-timeseries-revision-icon',
  template: `
    <div class="flex">
      <ng-container *ngIf="isInAnalyticsSandbox">
        <span
          [class.text-orange-400]="analyticsSandboxIsProcessing"
          [class.text-green-400]="analyticsSandboxSucceeded"
          [class.text-red-400]="analyticsSandboxFailed"
        >
          <app-analyticssandbox-icon></app-analyticssandbox-icon>
        </span>
      </ng-container>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeSeriesRevisionIconComponent {
  @Input() dataObject!: TimeSeriesRevision;

  constructor() {}

  get isInAnalyticsSandbox() {
    return isTimeSeriesInAnalyticsSandbox(this.dataObject);
  }
  get analyticsSandboxIsProcessing() {
    return timeSeriesAnalyticsSandboxIsProcessing(this.dataObject);
  }
  get analyticsSandboxSucceeded() {
    return timeSeriesAnalyticsSandboxSucceeded(this.dataObject);
  }
  get analyticsSandboxFailed() {
    return timeSeriesAnalyticsSandboxFailed(this.dataObject);
  }
}

@NgModule({
  declarations: [TimeSeriesRevisionIconComponent],
  exports: [TimeSeriesRevisionIconComponent],
  imports: [CommonModule, DataObjectIconComponentModule, IconsModule],
})
export class TimeSeriesRevisionIconComponentModule {}

@Component({
  selector: 'app-aggrid-timeseries-revision-icon',
  template: `<div class="pt-2 flex">
    <app-forwardcurve-revision-icon
      [class.text-orange-400]="isProcessing"
      [class.text-green-400]="isSucceeded"
      [class.text-red-400]="isFailed"
    >
    </app-forwardcurve-revision-icon>
    <app-timeseries-revision-icon
      [dataObject]="dataObject"
    ></app-timeseries-revision-icon>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridTimeSeriesRevisionIconComponent
  implements ICellRendererAngularComp
{
  dataObject!: TimeSeriesRevision;

  agInit(params: ICellRendererParams<any, any>): void {
    this.dataObject = params.data as TimeSeriesRevision;
  }

  refresh(params: ICellRendererParams<any, any>): boolean {
    this.agInit(params);
    return true;
  }

  get isProcessing() {
    return (
      this.dataObject.status === 'Processing' ||
      this.dataObject.status === 'Pending'
    );
  }
  get isSucceeded() {
    return this.dataObject.status === 'Completed';
  }
  get isFailed() {
    return this.dataObject.status === 'Failed';
  }
}

@NgModule({
  declarations: [AgGridTimeSeriesRevisionIconComponent],
  exports: [AgGridTimeSeriesRevisionIconComponent],
  imports: [
    CommonModule,
    DataObjectIconComponentModule,
    IconsModule,
    TimeSeriesRevisionIconComponentModule,
  ],
})
export class AgGridTimeSeriesRevisionIconComponentModule {}

@Component({
  selector: 'app-analyticssandbox-status-icon',
  template: `
    <div class="flex">
      <ng-container *ngIf="isInAnalyticsSandbox">
        <span
          [class.text-orange-400]="analyticsSandboxIsProcessing"
          [class.text-green-400]="analyticsSandboxSucceeded"
          [class.text-red-400]="analyticsSandboxFailed"
        >
          <app-analyticssandbox-icon></app-analyticssandbox-icon>
        </span>
      </ng-container>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalyticsSandboxStatusIconComponent {
  @Input() data!: {
    analyticsSandboxStatus: string | null;
    analyticsSandboxExpiryUtc: string | null;
    analyticsSandboxMetadataStatus: string | null;
  };

  constructor() {}

  get isInAnalyticsSandbox() {
    return isInAnalyticsSandbox(this.data.analyticsSandboxStatus);
  }
  get analyticsSandboxIsProcessing() {
    return analyticsSandboxIsProcessing(this.data.analyticsSandboxStatus);
  }
  get analyticsSandboxSucceeded() {
    return analyticsSandboxSucceeded(this.data.analyticsSandboxStatus);
  }
  get analyticsSandboxFailed() {
    return analyticsSandboxFailed(this.data.analyticsSandboxStatus);
  }
}

@NgModule({
  declarations: [AnalyticsSandboxStatusIconComponent],
  exports: [AnalyticsSandboxStatusIconComponent],
  imports: [CommonModule, DataObjectIconComponentModule, IconsModule],
})
export class AnalyticsSandboxStatusIconComponentModule {}

@Component({
  selector: 'app-aggrid-modelresult-icon',
  template: `<div class="pt-2 flex">
    <app-model-result-icon></app-model-result-icon>
    <app-timeseries-revision-icon
      [dataObject]="dataObject.timeSeriesMeta.revisions[0]"
    ></app-timeseries-revision-icon>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridModelResultIconComponent
  implements ICellRendererAngularComp
{
  dataObject!: ModelResultDataObject;

  agInit(params: ICellRendererParams<any, any>): void {
    this.dataObject = params.data as ModelResultDataObject;
  }

  refresh(params: ICellRendererParams<any, any>): boolean {
    this.agInit(params);
    return true;
  }
}

@Component({
  selector: 'app-aggrid-scenarioresult-icon',
  template: `<div class="pt-2 flex">
    <app-aggregated-scenario-result-icon></app-aggregated-scenario-result-icon>
    <app-analyticssandbox-status-icon
      [data]="dataObject.timeSeriesSetMeta"
    ></app-analyticssandbox-status-icon>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridScenarioResultIconComponent
  implements ICellRendererAngularComp
{
  dataObject!: ScenarioResultSetDataObject;

  agInit(params: ICellRendererParams<any, any>): void {
    this.dataObject = params.data as ScenarioResultSetDataObject;
  }

  refresh(params: ICellRendererParams<any, any>): boolean {
    this.agInit(params);
    return true;
  }
}

@Component({
  selector: 'app-aggrid-calculatedtimeseries-icon',
  template: `<div class="pt-2 flex">
    <app-calculator-icon></app-calculator-icon>
    <app-timeseries-revision-icon
      [dataObject]="dataObject.timeSeriesMeta.revisions[0]"
    ></app-timeseries-revision-icon>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridCalculatedTimeSeriesIconComponent
  implements ICellRendererAngularComp
{
  dataObject!: CalculatedTimeseriesDataObject;

  agInit(params: ICellRendererParams<any, any>): void {
    this.dataObject = params.data as CalculatedTimeseriesDataObject;
  }

  refresh(params: ICellRendererParams<any, any>): boolean {
    this.agInit(params);
    return true;
  }
}

@NgModule({
  declarations: [
    AgGridModelResultIconComponent,
    AgGridScenarioResultIconComponent,
    AgGridCalculatedTimeSeriesIconComponent,
  ],
  exports: [
    AgGridModelResultIconComponent,
    AgGridScenarioResultIconComponent,
    AgGridCalculatedTimeSeriesIconComponent,
  ],
  imports: [
    CommonModule,
    IconsModule,
    TimeSeriesRevisionIconComponentModule,
    AnalyticsSandboxStatusIconComponentModule,
  ],
})
export class AgGridModelResultIconComponentModule {}

@Component({
  selector: 'app-aggrid-valuationrun-icon',
  template: ` <div class="pt-2 flex">
    <span *ngIf="failed" class="text-red-400">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="w-6 h-6"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
        />
      </svg>
    </span>
    <span *ngIf="succeeded" class="text-green-400">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="w-6 h-6"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    </span>
    <span *ngIf="pending" class="text-blue-400">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="w-6 h-6"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    </span>
    <span *ngIf="processing" class="text-yellow-400">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="w-6 h-6"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    </span>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridValuationRunIconComponent
  implements ICellRendererAngularComp
{
  value: string = '';

  get failed() {
    return this.value === ValuationRunStatusValues.Failed;
  }
  get succeeded() {
    return this.value === ValuationRunStatusValues.Success;
  }
  get pending() {
    return this.value === ValuationRunStatusValues.Pending;
  }
  get processing() {
    return this.value === ValuationRunStatusValues.Processing;
  }

  agInit(params: ICellRendererParams<any, any>): void {
    this.value = params.value;
  }

  refresh(params: ICellRendererParams<any, any>): boolean {
    // force recreation of component (normal refresh not working)
    return false;
  }
}

@NgModule({
  declarations: [AgGridValuationRunIconComponent],
  exports: [AgGridValuationRunIconComponent],
  imports: [CommonModule, IconsModule, TimeSeriesRevisionIconComponentModule],
})
export class AgGridValuationRunIconComponentModule {}

@Component({
  selector: 'app-aggrid-efcr-isinrmvd-icon',
  template: ` <div class="pt-2">
    <span
      class="text-green-500"
      title="Is in RMVD"
      *ngIf="item.isInRmvd === true"
      ><app-check-circle-icon></app-check-circle-icon
    ></span>
    <span
      class="text-gray-500"
      title="Not in RMVD"
      *ngIf="item.isInRmvd === false"
      ><app-x-circle-icon></app-x-circle-icon
    ></span>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridEfcrIsInRmvdIconComponent
  implements ICellRendererAngularComp
{
  item!: EscalatedForwardCurveAsOfDate;

  agInit(params: ICellRendererParams<any, any>): void {
    this.item = params.data as EscalatedForwardCurveAsOfDate;
  }

  refresh(params: ICellRendererParams<any, any>): boolean {
    this.agInit(params);
    return true;
  }
}

@NgModule({
  declarations: [AgGridEfcrIsInRmvdIconComponent],
  exports: [AgGridEfcrIsInRmvdIconComponent],
  imports: [CommonModule, IconsModule],
})
export class AgGridEfcrIsInRmvdIconComponentModule {}

@Component({
  selector: 'app-aggrid-generation-profile-isinrmvd-icon',
  template: ` <div class="pt-2">
    <span
      class="text-green-500"
      title="Is in RMVD"
      *ngIf="item.isInRmvdStatus === 'IsInRmvd'"
      ><app-check-circle-icon></app-check-circle-icon
    ></span>
    <span
      class="text-yellow-400"
      *ngIf="item.isInRmvdStatus === 'Importing'"
      title="Importing into RMVD"
    >
      <app-circle-icon-processing></app-circle-icon-processing>
    </span>
    <span
      class="text-yellow-400"
      *ngIf="item.isInRmvdStatus === 'Exporting'"
      title="Exporting from RMVD"
    >
      <app-circle-icon-processing></app-circle-icon-processing>
    </span>
    <span
      class="text-red-400"
      title="Import or Export Failed"
      *ngIf="item.isInRmvdStatus === 'Error'"
      ><app-x-circle-icon></app-x-circle-icon
    ></span>
    <span
      class="text-gray-500"
      title="Not in RMVD"
      *ngIf="
        item.isInRmvdStatus === 'NotInRmvd' ||
        isEmptyNullOrUndefined(item.isInRmvdStatus)
      "
      ><app-x-circle-icon></app-x-circle-icon
    ></span>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridGenerationProfileIsInRmvdIconComponent
  implements ICellRendererAngularComp
{
  item!: HistoricDatasetGenerationProfile;
  isEmptyNullOrUndefined = isEmptyNullOrUndefined;

  agInit(params: ICellRendererParams<HistoricDatasetGenerationProfile>): void {
    this.item = params.data!;
  }

  refresh(): boolean {
    return false;
  }
}

@NgModule({
  declarations: [AgGridGenerationProfileIsInRmvdIconComponent],
  exports: [AgGridGenerationProfileIsInRmvdIconComponent],
  imports: [CommonModule, IconsModule],
})
export class AgGridGenerationProfileIsInRmvdIconComponentModule {}


@Component({
  selector: 'app-aggrid-schedule-icon',
  template: ` <ng-container *ngIf="showIcon"><svg version="1.1" style="margin-top: 10px;" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" class="w-5 h-5" viewBox="0 0 122.88 120.06" ><g><path d="M69.66,4.05c0-2.23,2.2-4.05,4.94-4.05c2.74,0,4.94,1.81,4.94,4.05v17.72c0,2.23-2.2,4.05-4.94,4.05 c-2.74,0-4.94-1.81-4.94-4.05V4.05L69.66,4.05z M91.37,57.03c4.26,0,8.33,0.85,12.05,2.39c3.87,1.6,7.34,3.94,10.24,6.84 c2.9,2.9,5.24,6.38,6.84,10.23c1.54,3.72,2.39,7.79,2.39,12.05c0,4.26-0.85,8.33-2.39,12.05c-1.6,3.87-3.94,7.34-6.84,10.24 c-2.9,2.9-6.38,5.24-10.23,6.84c-3.72,1.54-7.79,2.39-12.05,2.39c-4.26,0-8.33-0.85-12.05-2.39c-3.87-1.6-7.34-3.94-10.24-6.84 c-2.9-2.9-5.24-6.38-6.84-10.24c-1.54-3.72-2.39-7.79-2.39-12.05c0-4.26,0.85-8.33,2.39-12.05c1.6-3.87,3.94-7.34,6.84-10.24 c2.9-2.9,6.38-5.24,10.23-6.84C83.04,57.88,87.1,57.03,91.37,57.03L91.37,57.03z M89.01,75.37c0-0.76,0.31-1.45,0.81-1.95l0,0l0,0 c0.5-0.5,1.19-0.81,1.96-0.81c0.77,0,1.46,0.31,1.96,0.81c0.5,0.5,0.81,1.19,0.81,1.96v14.74l11.02,6.54l0.09,0.06 c0.61,0.39,1.01,0.98,1.17,1.63c0.17,0.68,0.09,1.42-0.28,2.06l-0.02,0.03c-0.02,0.04-0.04,0.07-0.07,0.1 c-0.39,0.6-0.98,1-1.62,1.16c-0.68,0.17-1.42,0.09-2.06-0.28l-12.32-7.29c-0.43-0.23-0.79-0.58-1.05-0.99 c-0.26-0.42-0.41-0.91-0.41-1.43h0L89.01,75.37L89.01,75.37L89.01,75.37z M109.75,70.16c-2.4-2.4-5.26-4.33-8.43-5.64 c-3.06-1.27-6.42-1.96-9.95-1.96s-6.89,0.7-9.95,1.96c-3.17,1.31-6.03,3.24-8.43,5.64c-2.4,2.4-4.33,5.26-5.64,8.43 c-1.27,3.06-1.96,6.42-1.96,9.95c0,3.53,0.7,6.89,1.96,9.95c1.31,3.17,3.24,6.03,5.64,8.43c2.4,2.4,5.26,4.33,8.43,5.64 c3.06,1.27,6.42,1.96,9.95,1.96s6.89-0.7,9.95-1.96c3.17-1.31,6.03-3.24,8.43-5.64c4.71-4.71,7.61-11.2,7.61-18.38 c0-3.53-0.7-6.89-1.96-9.95C114.08,75.42,112.15,72.56,109.75,70.16L109.75,70.16z M13.45,57.36c-0.28,0-0.53-1.23-0.53-2.74 c0-1.51,0.22-2.73,0.53-2.73h13.48c0.28,0,0.53,1.23,0.53,2.73c0,1.51-0.22,2.74-0.53,2.74H13.45L13.45,57.36z M34.94,57.36 c-0.28,0-0.53-1.23-0.53-2.74c0-1.51,0.22-2.73,0.53-2.73h13.48c0.28,0,0.53,1.23,0.53,2.73c0,1.51-0.22,2.74-0.53,2.74H34.94 L34.94,57.36z M56.43,57.36c-0.28,0-0.53-1.23-0.53-2.74c0-1.51,0.22-2.73,0.53-2.73h13.48c0.28,0,0.53,1.22,0.53,2.72 c-1.35,0.84-2.65,1.76-3.89,2.75H56.43L56.43,57.36z M13.48,73.04c-0.28,0-0.53-1.23-0.53-2.74c0-1.51,0.22-2.74,0.53-2.74h13.48 c0.28,0,0.53,1.23,0.53,2.74c0,1.51-0.22,2.74-0.53,2.74H13.48L13.48,73.04z M34.97,73.04c-0.28,0-0.53-1.23-0.53-2.74 c0-1.51,0.22-2.74,0.53-2.74h13.48c0.28,0,0.53,1.23,0.53,2.74c0,1.51-0.22,2.74-0.53,2.74H34.97L34.97,73.04z M13.51,88.73 c-0.28,0-0.53-1.23-0.53-2.74c0-1.51,0.22-2.74,0.53-2.74h13.48c0.28,0,0.53,1.23,0.53,2.74c0,1.51-0.22,2.74-0.53,2.74H13.51 L13.51,88.73z M35,88.73c-0.28,0-0.53-1.23-0.53-2.74c0-1.51,0.22-2.74,0.53-2.74h13.48c0.28,0,0.53,1.23,0.53,2.74 c0,1.51-0.22,2.74-0.53,2.74H35L35,88.73z M25.29,4.05c0-2.23,2.2-4.05,4.94-4.05c2.74,0,4.94,1.81,4.94,4.05v17.72 c0,2.23-2.21,4.05-4.94,4.05c-2.74,0-4.94-1.81-4.94-4.05V4.05L25.29,4.05z M5.44,38.74h94.08v-20.4c0-0.7-0.28-1.31-0.73-1.76 c-0.45-0.45-1.09-0.73-1.76-0.73h-9.02c-1.51,0-2.74-1.23-2.74-2.74c0-1.51,1.23-2.74,2.74-2.74h9.02c2.21,0,4.19,0.89,5.64,2.34 c1.45,1.45,2.34,3.43,2.34,5.64v32.39c-1.8-0.62-3.65-1.12-5.55-1.49v-5.06h0.06H5.44v52.83c0,0.7,0.28,1.31,0.73,1.76 c0.45,0.45,1.09,0.73,1.76,0.73h44.71c0.51,1.9,1.15,3.75,1.92,5.53H7.98c-2.2,0-4.19-0.89-5.64-2.34C0.89,101.26,0,99.28,0,97.07 V18.36c0-2.2,0.89-4.19,2.34-5.64c1.45-1.45,3.43-2.34,5.64-2.34h9.63c1.51,0,2.74,1.23,2.74,2.74c0,1.51-1.23,2.74-2.74,2.74H7.98 c-0.7,0-1.31,0.28-1.76,0.73c-0.45,0.45-0.73,1.09-0.73,1.76v20.4H5.44L5.44,38.74z M43.07,15.85c-1.51,0-2.74-1.23-2.74-2.74 c0-1.51,1.23-2.74,2.74-2.74h18.36c1.51,0,2.74,1.23,2.74,2.74c0,1.51-1.23,2.74-2.74,2.74H43.07L43.07,15.85z"/></g></svg></ng-container>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridScheduleIconObjectComponent implements ICellRendererAngularComp {
  // dataObject!: DataObjectTypes;
  showIcon = false;

  agInit(params: ICellRendererParams<any, any>): any {
    const vtId = params.data.id;
    if(params.data.scheduled && params.data.scheduled.length){
      const isScheduled = params.data.scheduled.some((scheduledItem: any) => scheduledItem === vtId);
      this.showIcon = isScheduled;
    }
  }

  refresh(params: ICellRendererParams<any, any>): boolean {
    // this.agInit(params);
    return true;
  }
}

@NgModule({
  declarations: [AgGridScheduleIconObjectComponent],
  exports: [AgGridScheduleIconObjectComponent],
  imports: [CommonModule, IconsModule],
})
export class AgGridScheduleIconComponentModule {}
